import React, { useState, useEffect, useRef, useMemo, Suspense, lazy } from 'react';
import BBALoader from '../components/BBALoader';
import { Plus, Search, Star, MoreVertical, Edit, Trash2, Mail, Phone, Building2, User, X, Lock, Unlock } from 'lucide-react';
import { getAccounts, addAccount, updateAccount, deleteAccount, updateAccountStage, getCrmContactDisplayPhotoUrl, getCrmCompanyLogoDisplayUrl, getCrmTeamMemberDisplayPhotoUrl } from '../services/firestoreService';
import { fetchPortalUsersLinkedToAccounts } from '../services/accountTeamMembersService';
import { fetchGmailMessagesForEmail } from '../services/gmailService';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import BackButton from '../components/BackButton';
import AddButton from '../components/AddButton';
import PortalBackground from '../components/PortalBackground';
import { useResponsive } from '../contexts/ResponsiveContext';
import LeadSubstatusCheatsheet from '../components/LeadSubstatusCheatsheet';
import { normalizeLeadSigningChance, SIGNING_CHANCE_META, SIGNING_CHANCE_VALUES } from '../utils/leadSigningChance';
import { CONTACT_FLAG_THRESHOLD_DAYS } from '../utils/contactFlags';
import { parseAddressLine, websiteFromEmail } from '../utils/addressParse';
import { db, storage } from '../firebase';
import { doc, getDoc, updateDoc, deleteField } from 'firebase/firestore';
import { isEndedClient, resolveClientLifecycle, describeClientLifecycle, toDateSafe, getProgramTier } from '../utils/accountLifecycle';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getLeadFollowUp } from '../utils/leadFollowUp';

// Map geometry (~110KB) and the ZIP table (~600KB) only load with the Map view.
const ClientMapView = lazy(() => import('../components/crm/ClientMapView'));
const LeadFollowUpBoard = lazy(() => import('../components/crm/LeadFollowUpBoard'));

// Clients view picker: these sit alongside Lost in the dropdown instead of the tier list.
const CLIENT_VIEW_LIFECYCLE = '__lifecycle';
const CLIENT_VIEW_MAP = '__map';
// Leads view picker: Stages / Prospects / Map View (shared value) / Follow-Up / Lost.
const LEAD_VIEW_PROSPECTS = '__prospects';
const LEAD_VIEW_FOLLOW_UP = '__followup';
// Dropdown views per tab, in display order — the first is the default until someone stars one.
// A starred default is only honored if it's still one of these. Lost: its own board for Clients,
// a table for Leads — never a column on Stages.
const TAB_VIEWS = {
  Clients: [
    { value: CLIENT_VIEW_LIFECYCLE, label: 'Lifecycle' },
    { value: CLIENT_VIEW_MAP, label: 'Map View' },
    { value: 'All', label: 'Stages' },
    { value: 'Lost', label: 'Lost' },
  ],
  Leads: [
    { value: 'All', label: 'Stages' },
    { value: LEAD_VIEW_FOLLOW_UP, label: 'Follow-Up' },
    { value: CLIENT_VIEW_MAP, label: 'Map View' },
    { value: LEAD_VIEW_PROSPECTS, label: 'Prospects' },
    { value: 'Lost', label: 'Lost' },
  ],
};
const viewModeForSelection = (value, tab) => (
  value === 'All' ? 'kanban'
    : value === CLIENT_VIEW_LIFECYCLE ? 'lifecycle'
    : value === CLIENT_VIEW_MAP ? 'map'
    : value === LEAD_VIEW_PROSPECTS ? 'prospects'
    : value === LEAD_VIEW_FOLLOW_UP ? 'followup'
    : value === 'Lost' && tab === 'Clients' ? 'lost'
    : 'table'
);
// Stages the Leads map shows until someone changes the stage chips.
const LEAD_MAP_DEFAULT_STAGES = ['Lead Received', 'Discovery Call', 'Assessment', 'Onboarding Call', 'Proposal'];

// Lifecycle board columns — months since the client started (program.startDate,
// else conversion, else creation). A future start date counts as month 0.
const LIFECYCLE_BUCKETS = [
  { id: 'months-1-2', title: '1–2 Months', minMonths: 0, maxMonths: 3, color: '#3B82F6' },
  { id: 'months-3-6', title: '3–6 Months', minMonths: 3, maxMonths: 6, color: '#8B5CF6' },
  { id: 'months-6-12', title: '6–12 Months', minMonths: 6, maxMonths: 12, color: '#F59E0B' },
  { id: 'months-12-plus', title: 'Over a Year', minMonths: 12, maxMonths: Infinity, color: '#10B981' },
];

const getClientStartDate = (account) =>
  toDateSafe(account?.program?.startDate) ||
  toDateSafe(account?.convertedToClientAt) ||
  toDateSafe(account?.createdAt);

const wholeMonthsSince = (start, now = new Date()) => {
  let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) months -= 1;
  return Math.max(months, 0);
};

const lifecycleBucketFor = (months) =>
  LIFECYCLE_BUCKETS.find(b => months >= b.minMonths && months < b.maxMonths);

// Prospects board columns — time in the pipeline. Same breaks as Lifecycle, with the first
// bucket split so prospects under 30 days stand apart.
const PIPELINE_BUCKETS = [
  { id: 'days-0-30', title: 'Under 30 Days', minMonths: 0, maxMonths: 1, color: '#3B82F6' },
  { id: 'months-1-2', title: '1–2 Months', minMonths: 1, maxMonths: 3, color: '#06B6D4' },
  { id: 'months-3-6', title: '3–6 Months', minMonths: 3, maxMonths: 6, color: '#8B5CF6' },
  { id: 'months-6-12', title: '6–12 Months', minMonths: 6, maxMonths: 12, color: '#F59E0B' },
  { id: 'months-12-plus', title: 'Over a Year', minMonths: 12, maxMonths: Infinity, color: '#EF4444' },
];

const DAY_MS = 24 * 60 * 60 * 1000;

/** When a lead entered the pipeline: createdAt, else its earliest status-history entry. */
const getLeadPipelineStart = (account) => {
  const created = toDateSafe(account?.createdAt);
  if (created) return created;
  const history = Array.isArray(account?.statusHistory) ? account.statusHistory : [];
  const dates = history.map(entry => toDateSafe(entry?.date)).filter(Boolean);
  return dates.length ? new Date(Math.min(...dates.map(d => d.getTime()))) : null;
};

// Card corner label: weeks under a month ("3 wk"), months after ("7 mo").
const formatTenure = (start, end) => {
  const months = wholeMonthsSince(start, end);
  if (months >= 1) return `${months} mo`;
  const weeks = Math.floor((end.getTime() - start.getTime()) / (7 * 24 * 60 * 60 * 1000));
  return weeks < 1 ? '<1 wk' : `${weeks} wk`;
};

/** How long a client was/has been with us, measured up to `end` (now, or when they ended). */
const describeTenure = (account, end) => {
  const start = getClientStartDate(account);
  if (!start) {
    return { start: null, bucket: LIFECYCLE_BUCKETS[0], cornerLabel: 'No date', cornerTitle: 'No start date on file' };
  }
  if (start > end) {
    return {
      start,
      bucket: LIFECYCLE_BUCKETS[0],
      cornerLabel: `Starts ${start.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}`,
      cornerTitle: `Starts ${start.toLocaleDateString()}`,
    };
  }
  return {
    start,
    bucket: lifecycleBucketFor(wholeMonthsSince(start, end)),
    cornerLabel: formatTenure(start, end),
    cornerTitle: `Started ${start.toLocaleDateString()}`,
  };
};

// Client card subtitle: full name for one contact, first names when the team has more.
const clientNamesLine = (account, extraTeamMembers = []) => {
  const firstOf = (m) =>
    (m?.firstName || (m?.name ? String(m.name).split(' ')[0] : '') || '').trim();
  const primaryFirst = (account.contact?.firstName || '').trim();
  const primaryLast = (account.contact?.lastName || '').trim();
  const extraFirsts = extraTeamMembers.map(firstOf).filter(Boolean);
  if (extraFirsts.length > 0) return [primaryFirst, ...extraFirsts].filter(Boolean).join(', ');
  return `${primaryFirst} ${primaryLast}`.trim();
};

// Lead substatuses by stage
const LEAD_SUBSTATUSES = {
  'Lead Received': [
    { 
      name: 'Initial Message Sent (Unopened)', 
      icon: 'leademail.png', 
      description: 'First message sent but not opened yet'
    },
    { 
      name: 'Initial Message Sent (Opened)', 
      icon: 'leademailopened.png', 
      description: 'First message sent and opened by client'
    },
    { 
      name: 'First Drip Message Sent', 
      icon: 'leademailtwo.png', 
      description: 'Second follow-up message sent'
    },
    { 
      name: 'First Drip Message Sent (Client Opened)', 
      icon: 'leademailtwoopened.png', 
      description: 'Second follow-up message opened by client'
    },
    { 
      name: 'Second Drip Message Sent', 
      icon: 'leademailthree.png', 
      description: 'Second drip follow-up message sent'
    },
    { 
      name: 'Second Drip Message Sent (Opened)', 
      icon: 'leademailthreeopened.png', 
      description: 'Second drip follow-up message opened by client'
    },
    { 
      name: 'Final Message Sent', 
      icon: 'leademaillast.png', 
      description: 'Final follow-up message sent'
    },
    { 
      name: 'Final Message Sent (Opened)', 
      icon: 'leademaillastopened.png', 
      description: 'Final follow-up message opened by client'
    },
    { 
      name: 'Cold Lead', 
      icon: 'coldlead.svg', 
      description: 'Lead that has not responded to any messages'
    }
  ],
  'Discovery Call': [
    { 
      name: 'Scheduled (Confirmed)', 
      icon: 'scheduled.svg', 
      description: 'Discovery call scheduled and confirmed'
    },
    { 
      name: 'Scheduled (Not Confirmed)', 
      icon: 'schedulednoconfirm.svg', 
      description: 'Discovery call scheduled but not yet confirmed'
    },
    { 
      name: 'Ghosted', 
      icon: 'ghost.svg', 
      description: 'Lead has stopped responding to communications'
    },
    { 
      name: 'Rescheduled (Not Confirmed)', 
      icon: 'rescheduled.svg', 
      description: 'Discovery call rescheduled but not yet confirmed'
    },
    { 
      name: 'Rescheduled (Confirmed)', 
      icon: 'rescheduledconfirm.svg', 
      description: 'Discovery call rescheduled and confirmed'
    },
    { 
      name: 'Cold Lead', 
      icon: 'coldlead.svg', 
      description: 'Lead that has not responded to any communications'
    }
  ],
  'Assessment': [
    { 
      name: 'Portal Invite Sent', 
      icon: 'invitesent.svg', 
      description: 'Assessment portal invite link has been created'
    },
    { 
      name: 'Signed Up For Portal', 
      icon: 'portalsignup.png', 
      description: 'Lead has signed up for the portal'
    },
    { 
      name: 'NDA Signed', 
      icon: 'ndadone.png', 
      description: 'Lead has signed the NDA'
    },
    { 
      name: 'Assessment Done', 
      icon: 'assesdone.png', 
      description: 'Lead has completed the assessment'
    },
    { 
      name: 'Solutions Chosen', 
      icon: 'solutionsdone.png', 
      description: 'Lead has chosen their solutions'
    },
    { 
      name: 'Cold Lead', 
      icon: 'coldlead.svg', 
      description: 'Lead that has not responded to any communications'
    }
  ],
  'Proposal': [
    { 
      name: 'Not Started', 
      icon: 'nostart.svg', 
      description: 'Proposal has not been started yet'
    },
    { 
      name: 'Draft', 
      icon: 'draft.svg', 
      description: 'Proposal is in draft stage'
    },
    { 
      name: 'Pending Payment', 
      icon: 'hourglass.svg', 
      description: 'Proposal sent and waiting for payment'
    }
  ],
  'Onboarding Call': [
    { 
      name: 'Scheduled (Confirmed)', 
      icon: 'scheduled.svg', 
      description: 'Onboarding call scheduled and confirmed'
    },
    { 
      name: 'Scheduled (Not Confirmed)', 
      icon: 'schedulednoconfirm.svg', 
      description: 'Onboarding call scheduled but not yet confirmed'
    },
    { 
      name: 'Ghosted', 
      icon: 'ghost.svg', 
      description: 'Lead has stopped responding to communications'
    },
    { 
      name: 'Rescheduled (Not Confirmed)', 
      icon: 'rescheduled.svg', 
      description: 'Onboarding call rescheduled but not yet confirmed'
    },
    { 
      name: 'Rescheduled (Confirmed)', 
      icon: 'rescheduledconfirm.svg', 
      description: 'Onboarding call rescheduled and confirmed'
    },
    { 
      name: 'Cold Lead', 
      icon: 'coldlead.svg', 
      description: 'Lead that has not responded to any communications'
    }
  ],
};

// Hardcoded account types and stages
const ACCOUNT_TYPES = [
  {
    id: 'leads',
    label: 'Leads',
    value: 'leads',
    color: '#EF4444',
    active: true,
    defaultStages: [
      { id: 0, title: 'Prospects', color: '#EF4444', order: 0 },
      { id: 1, title: 'Lead Received', color: '#3B82F6', order: 1 },
      { id: 2, title: 'Discovery Call', color: '#8B5CF6', order: 2 },
      { id: 3, title: 'Assessment', color: '#10B981', order: 3 },
      { id: 4, title: 'Onboarding Call', color: '#F59E0B', order: 4 },
      { id: 5, title: 'Proposal', color: '#EC4899', order: 5 }
    ]
  },
  {
    id: 'clients',
    label: 'Clients',
    value: 'clients',
    color: '#10B981',
    active: true,
    defaultStages: [
      { id: 0, title: 'Custom', color: '#6366F1', order: 0 },
      { id: 1, title: 'Lite', color: '#3B82F6', order: 1 },
      { id: 2, title: 'Plus', color: '#8B5CF6', order: 2 },
      { id: 3, title: 'Pro', color: '#10B981', order: 3 }
    ]
  }
];

// Stage colors by type
const stageColorsByType = {
  'Leads': {
    'Prospects': { text: 'text-red-600', border: 'border-red-600' },
    'Lead Received': { text: 'text-blue-600', border: 'border-blue-600' },
    'Discovery Call': { text: 'text-purple-600', border: 'border-purple-600' },
    'Assessment': { text: 'text-emerald-600', border: 'border-emerald-600' },
    'Onboarding Call': { text: 'text-amber-600', border: 'border-amber-600' },
    'Proposal': { text: 'text-pink-600', border: 'border-pink-600' },
    'Lost': { text: 'text-gray-600', border: 'border-gray-600' }
  },
  'Clients': {
    'Custom': { text: 'text-indigo-600', border: 'border-indigo-600' },
    'Lite': { text: 'text-blue-600', border: 'border-blue-600' },
    'Plus': { text: 'text-purple-600', border: 'border-purple-600' },
    'Pro': { text: 'text-green-600', border: 'border-green-600' },
    'Hourly': { text: 'text-amber-600', border: 'border-amber-600' },
    '5 Hours': { text: 'text-pink-600', border: 'border-pink-600' },
    '10 Hours': { text: 'text-cyan-600', border: 'border-cyan-600' },
    'A La Carte': { text: 'text-purple-600', border: 'border-purple-600' },
    'Lost': { text: 'text-gray-600', border: 'border-gray-600' }
  }
};

/** CRM Clients: primary contact + company logo + additional portal team (same row). */
function ClientCrmAvatarCluster({ account, extraTeamMembers, isMobile, tableLayout, maxExtraTeamMembers }) {
  const dim = isMobile ? 'w-[40px] h-[40px]' : 'w-[50px] h-[50px]';
  const textMain = isMobile ? 'text-base' : 'text-lg';
  const logoUrl = getCrmCompanyLogoDisplayUrl(account);
  const primaryPhoto = getCrmContactDisplayPhotoUrl(account);
  const initials =
    `${account.contact?.firstName?.[0] || ''}${account.contact?.lastName?.[0] || ''}`.trim() || '?';

  const rowMb = tableLayout ? '' : isMobile ? 'mb-1' : 'mb-2';
  const extras = extraTeamMembers || [];
  const visibleExtras =
    typeof maxExtraTeamMembers === 'number'
      ? extras.slice(0, Math.max(0, maxExtraTeamMembers))
      : extras;

  return (
    <div className={`flex flex-row flex-wrap items-center justify-center gap-1 ${rowMb}`}>
      <div
        className={`${dim} shrink-0 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-600 ring-1 ring-gray-200 dark:ring-gray-500 flex items-center justify-center`}
      >
        {primaryPhoto ? (
          <img
            src={primaryPhoto}
            alt={`${account.contact?.firstName || ''} ${account.contact?.lastName || ''}`.trim() || 'Contact'}
            className={`${dim} object-cover`}
            onError={(e) => {
              e.target.style.display = 'none';
              const next = e.target.nextSibling;
              if (next) next.style.display = 'flex';
            }}
          />
        ) : null}
        <div
          className={`${dim} bg-gray-400 rounded-full items-center justify-center text-white font-semibold ${textMain} ${primaryPhoto ? 'hidden' : 'flex'}`}
        >
          {initials}
        </div>
      </div>
      {logoUrl ? (
        <div
          className={`${dim} shrink-0 rounded-full overflow-hidden bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 flex items-center justify-center`}
          title={account.company ? `${account.company} logo` : 'Company logo'}
        >
          <img
            src={logoUrl}
            alt=""
            className="max-w-[88%] max-h-[88%] object-contain"
          />
        </div>
      ) : null}
      {visibleExtras.map((m) => {
        const ti =
          `${m.firstName?.[0] || ''}${m.lastName?.[0] || ''}`.trim() ||
          (m.name && m.name.length >= 2 ? m.name.slice(0, 2) : m.name?.[0] || '?');
        const teamPhotoUrl = getCrmTeamMemberDisplayPhotoUrl(account, m.uid) || m.profileImage;
        return (
          <div
            key={m.uid}
            className={`${dim} shrink-0 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-600 ring-1 ring-gray-200 dark:ring-gray-500 flex items-center justify-center`}
            title={m.name || m.email || 'Team member'}
          >
            {teamPhotoUrl ? (
              <>
                <img
                  src={teamPhotoUrl}
                  alt=""
                  className={`${dim} object-cover`}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const next = e.target.nextSibling;
                    if (next) next.style.display = 'flex';
                  }}
                />
                <div
                  className={`${dim} hidden items-center justify-center rounded-full bg-gray-400 font-semibold text-white ${textMain}`}
                >
                  {ti.toUpperCase()}
                </div>
              </>
            ) : (
              <div
                className={`${dim} flex items-center justify-center rounded-full bg-gray-400 font-semibold text-white ${textMain}`}
              >
                {ti.toUpperCase()}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Lead card icon row: company logo, substatus icon, contact photo (mirrors ClientCrmAvatarCluster, smaller). */
function LeadCrmAvatarCluster({ account, isMobile, tableLayout, substatusIcon }) {
  const dim = isMobile ? 'w-[32px] h-[32px]' : 'w-[38px] h-[38px]';
  const textMain = isMobile ? 'text-xs' : 'text-sm';
  const logoUrl = getCrmCompanyLogoDisplayUrl(account);
  const primaryPhoto = getCrmContactDisplayPhotoUrl(account);
  const initials =
    `${account.contact?.firstName?.[0] || ''}${account.contact?.lastName?.[0] || ''}`.trim() || '?';
  const rowMb = tableLayout ? '' : isMobile ? 'mb-1' : 'mb-2';

  return (
    <div className={`flex flex-row flex-nowrap items-center justify-center gap-1 ${rowMb}`}>
      {/* Company logo */}
      <div
        className={`${dim} shrink-0 rounded-full overflow-hidden bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 flex items-center justify-center`}
        title={account.company ? `${account.company} logo` : 'Company logo'}
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt=""
            className="max-w-[88%] max-h-[88%] object-contain"
            onError={(e) => {
              e.target.style.display = 'none';
              const next = e.target.nextSibling;
              if (next) next.style.display = 'flex';
            }}
          />
        ) : null}
        <div
          className={`${dim} bg-gray-100 dark:bg-gray-600 rounded-full items-center justify-center text-gray-400 dark:text-gray-300 ${logoUrl ? 'hidden' : 'flex'}`}
        >
          <Building2 className={isMobile ? 'w-4 h-4' : 'w-5 h-5'} strokeWidth={1.75} />
        </div>
      </div>
      {/* Substatus icon */}
      <div className={`${dim} shrink-0 flex items-center justify-center`} title={account.substatus || 'New Lead'}>
        <img
          src={process.env.PUBLIC_URL + '/' + substatusIcon}
          alt={account.substatus || 'New Lead'}
          className={`${dim} object-contain`}
          onError={(e) => {
            e.target.style.display = 'none';
            const next = e.target.nextSibling;
            if (next) next.style.display = 'block';
          }}
        />
        <div className={`${dim} bg-gray-400 rounded hidden`} />
      </div>
      {/* Contact photo */}
      <div
        className={`${dim} shrink-0 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-600 ring-1 ring-gray-200 dark:ring-gray-500 flex items-center justify-center`}
        title={`${account.contact?.firstName || ''} ${account.contact?.lastName || ''}`.trim() || 'Contact'}
      >
        {primaryPhoto ? (
          <img
            src={primaryPhoto}
            alt={`${account.contact?.firstName || ''} ${account.contact?.lastName || ''}`.trim() || 'Contact'}
            className={`${dim} object-cover`}
            onError={(e) => {
              e.target.style.display = 'none';
              const next = e.target.nextSibling;
              if (next) next.style.display = 'flex';
            }}
          />
        ) : null}
        <div
          className={`${dim} bg-gray-400 rounded-full items-center justify-center text-white font-semibold ${textMain} ${primaryPhoto ? 'hidden' : 'flex'}`}
        >
          {initials.toUpperCase()}
        </div>
      </div>
    </div>
  );
}

const Accounts = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile, isTablet } = useResponsive();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('Leads');
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedStage, setCopiedStage] = useState(null);
  const [formData, setFormData] = useState({
    type: 'Leads',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    website: '',
    photoURL: '',
    addressLine: '',
    address: '',
    zipCode: '',
    city: '',
    state: '',
    stage: 'Lead Received',
    substatus: '',
    estimatedProgram: '',
    leadSource: 'Website'
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  // Kanban column currently hovered during a card drag (for the drop highlight)
  const [dragOverStage, setDragOverStage] = useState(null);
  const [selectedStage, setSelectedStage] = useState('All');
  const [viewMode, setViewMode] = useState('kanban');
  // Clients ▸ Lost board columns: 'program' (tier) or 'lifecycle' (how long they stayed)
  const [lostGroupBy, setLostGroupBy] = useState('program');
  // Leads ▸ Map filters: pipeline stages on (Prospects off), every signing chance on.
  const [mapStageFilter, setMapStageFilter] = useState(() => new Set(LEAD_MAP_DEFAULT_STAGES));
  const [mapChanceFilter, setMapChanceFilter] = useState(() => new Set(SIGNING_CHANCE_VALUES));
  const toggleInSet = (setter, value) =>
    setter(prev => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  const [showCheatsheet, setShowCheatsheet] = useState(false);
  
  /** accountId -> portal users on account excluding primary (for client cards). */
  const [clientsExtraTeamByAccountId, setClientsExtraTeamByAccountId] = useState({});

  const { currentUser } = useAuth();

  const crmClientAccountIdsKey = useMemo(
    () =>
      accounts
        .filter((a) => a.type === 'Clients')
        .map((a) => a.id)
        .sort()
        .join('|'),
    [accounts]
  );

  useEffect(() => {
    let cancelled = false;
    const clients = accounts.filter((a) => a.type === 'Clients');
    if (clients.length === 0) {
      setClientsExtraTeamByAccountId({});
      return;
    }
    (async () => {
      try {
        const map = await fetchPortalUsersLinkedToAccounts(clients);
        if (cancelled) return;
        const next = {};
        map.forEach((arr, id) => {
          next[id] = (arr || []).filter((m) => !m.isPrimary);
        });
        setClientsExtraTeamByAccountId(next);
      } catch (err) {
        console.error('Error loading portal team for CRM client cards:', err);
        if (!cancelled) setClientsExtraTeamByAccountId({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [crmClientAccountIdsKey]);

  // Helper function to get icon filename from substatus name
  const getSubstatusIcon = (substatusName) => {
    // Search through all stages to find the substatus
    for (const stage in LEAD_SUBSTATUSES) {
      const substatus = LEAD_SUBSTATUSES[stage].find(s => s.name === substatusName);
      if (substatus) {
        return substatus.icon;
      }
    }
    return 'newlead.svg'; // Default fallback
  };

  // Helper function to format date for display
  const formatDate = (dateValue) => {
    if (!dateValue) return null;
    try {
      let date;
      // Handle date-only string (YYYY-MM-DD)
      if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        const [year, month, day] = dateValue.split('-').map(Number);
        date = new Date(year, month - 1, day);
      } else if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      } else if (dateValue.toDate && typeof dateValue.toDate === 'function') {
        date = dateValue.toDate();
      } else if (dateValue.seconds) {
        date = new Date(dateValue.seconds * 1000);
      } else {
        date = new Date(dateValue);
      }
      if (isNaN(date.getTime())) return null;
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (error) {
      return null;
    }
  };

  // Helper function to get discovery call date for sorting
  const getDiscoveryCallDate = (account) => {
    if (!account?.discoveryCallDate || account.discoveryCallDate === 'TBD') return null;
    try {
      // Handle date-only string (YYYY-MM-DD)
      if (typeof account.discoveryCallDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(account.discoveryCallDate)) {
        const [year, month, day] = account.discoveryCallDate.split('-').map(Number);
        return new Date(year, month - 1, day);
      } else if (typeof account.discoveryCallDate === 'string') {
        return new Date(account.discoveryCallDate);
      } else if (account.discoveryCallDate.toDate && typeof account.discoveryCallDate.toDate === 'function') {
        return account.discoveryCallDate.toDate();
      } else if (account.discoveryCallDate.seconds) {
        return new Date(account.discoveryCallDate.seconds * 1000);
      }
      return new Date(account.discoveryCallDate);
    } catch (error) {
      return null;
    }
  };

  // Helper function to get onboarding call date for sorting
  const getOnboardingCallDate = (account) => {
    if (!account?.onboardingCallDate || account.onboardingCallDate === 'TBD') return null;
    try {
      if (typeof account.onboardingCallDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(account.onboardingCallDate)) {
        const [year, month, day] = account.onboardingCallDate.split('-').map(Number);
        return new Date(year, month - 1, day);
      } else if (typeof account.onboardingCallDate === 'string') {
        return new Date(account.onboardingCallDate);
      } else if (account.onboardingCallDate.toDate && typeof account.onboardingCallDate.toDate === 'function') {
        return account.onboardingCallDate.toDate();
      } else if (account.onboardingCallDate.seconds) {
        return new Date(account.onboardingCallDate.seconds * 1000);
      }
      return new Date(account.onboardingCallDate);
    } catch (error) {
      return null;
    }
  };

  // Order call-date columns: real dates first (soonest upcoming at top), then
  // TBD, then anything with no date at all. getDiscoveryCallDate/
  // getOnboardingCallDate return null for 'TBD', so check the raw field to
  // separate a deliberate TBD from a blank.
  const compareByCallDate = (a, b, getDate, field) => {
    const dateA = getDate(a);
    const dateB = getDate(b);
    if (dateA && dateB) return dateA.getTime() - dateB.getTime();
    if (dateA) return -1;
    if (dateB) return 1;

    // Neither has a real date — a TBD still outranks a blank.
    const tbdA = a?.[field] === 'TBD';
    const tbdB = b?.[field] === 'TBD';
    if (tbdA === tbdB) return 0;
    return tbdA ? -1 : 1;
  };

  // Helper function to check if an account has been marked lost. Leads carry it
  // on stage/result; clients can also carry it on the program map (program.status
  // mirrors onto stage, but program.clientStatus/status are written separately).
  const isLostAccount = (account) => {
    // Clients: one lifecycle rule — Lost, Canceled (incl. a cancel date that passed) or Archived.
    if (account?.type === 'Clients') return isEndedClient(account);
    const markers = [
      account?.stage,
      account?.result,
      account?.status,
      account?.program?.status,
      account?.program?.clientStatus,
    ];
    return markers.some(
      (value) => typeof value === 'string' && value.trim().toLowerCase() === 'lost'
    );
  };

  // Helper function to get the date an account was lost, for sorting the Lost view.
  // Nothing writes a dedicated lost timestamp for leads, so fall back through
  // program end date (clients) -> statusHistory -> updatedAt -> createdAt.
  const getLostDate = (account) => {
    const toDate = (value) => {
      if (!value) return null;
      try {
        let date;
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
          const [year, month, day] = value.split('-').map(Number);
          date = new Date(year, month - 1, day);
        } else if (typeof value === 'string') {
          date = new Date(value);
        } else if (value.toDate && typeof value.toDate === 'function') {
          date = value.toDate();
        } else if (value.seconds) {
          date = new Date(value.seconds * 1000);
        } else {
          date = new Date(value);
        }
        return isNaN(date.getTime()) ? null : date;
      } catch (error) {
        return null;
      }
    };

    if (account?.lostAt) {
      const explicit = toDate(account.lostAt);
      if (explicit) return explicit;
    }

    if (account?.program?.endDate) {
      const programEnd = toDate(account.program.endDate);
      if (programEnd) return programEnd;
    }

    if (Array.isArray(account?.statusHistory)) {
      const lostEntries = account.statusHistory
        .filter(entry => {
          const label = `${entry?.status || ''} ${entry?.substatus || ''} ${entry?.action || ''}`;
          return /\blost\b/i.test(label);
        })
        .map(entry => toDate(entry?.date))
        .filter(Boolean);
      if (lostEntries.length > 0) {
        return lostEntries.sort((a, b) => b.getTime() - a.getTime())[0];
      }
    }

    return toDate(account?.updatedAt) || toDate(account?.createdAt);
  };

  // Helper function to check if discovery call date is today
  const isDiscoveryCallToday = (dateValue) => {
    if (!dateValue) return false;
    try {
      let date;
      // Handle date-only string (YYYY-MM-DD)
      if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        const [year, month, day] = dateValue.split('-').map(Number);
        date = new Date(year, month - 1, day);
      } else if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      } else if (dateValue.toDate && typeof dateValue.toDate === 'function') {
        date = dateValue.toDate();
      } else if (dateValue.seconds) {
        date = new Date(dateValue.seconds * 1000);
      } else {
        date = new Date(dateValue);
      }
      if (isNaN(date.getTime())) return false;

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const targetDate = new Date(date);
      targetDate.setHours(0, 0, 0, 0);

      return targetDate.getTime() === today.getTime();
    } catch (error) {
      return false;
    }
  };

  // Helper function to format discovery call date with smart formatting
  const formatDiscoveryCallDate = (dateValue) => {
    if (!dateValue) return null;
    // Handle TBD case
    if (dateValue === 'TBD') return 'TBD';
    try {
      let date;
      // Handle date-only string (YYYY-MM-DD)
      if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        const [year, month, day] = dateValue.split('-').map(Number);
        date = new Date(year, month - 1, day);
      } else if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      } else if (dateValue.toDate && typeof dateValue.toDate === 'function') {
        date = dateValue.toDate();
      } else if (dateValue.seconds) {
        date = new Date(dateValue.seconds * 1000);
      } else {
        date = new Date(dateValue);
      }
      if (isNaN(date.getTime())) return null;

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const targetDate = new Date(date);
      targetDate.setHours(0, 0, 0, 0);

      const diffTime = targetDate - today;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayName = dayNames[targetDate.getDay()];
      const getMonday = (d) => {
        const d2 = new Date(d);
        const day = d2.getDay();
        const diff = day === 0 ? -6 : 1 - day;
        d2.setDate(d2.getDate() + diff);
        d2.setHours(0, 0, 0, 0);
        return d2;
      };
      const todayMonday = getMonday(today);
      const targetMonday = getMonday(targetDate);

      // Check if it's today
      if (diffDays === 0) {
        return 'Today';
      }

      // Check if it's tomorrow (future only)
      if (diffDays === 1) {
        return 'Tomorrow';
      }

      // Check if it's yesterday (past only)
      if (diffDays === -1) {
        return 'Yesterday';
      }

      // Past dates (Assessment, Proposal - when call already happened)
      if (diffDays < 0) {
        if (targetMonday.getTime() === todayMonday.getTime()) {
          return `This ${dayName}`;
        }
        const lastWeekMonday = new Date(todayMonday);
        lastWeekMonday.setDate(lastWeekMonday.getDate() - 7);
        if (targetMonday.getTime() === lastWeekMonday.getTime()) {
          return `Last ${dayName}`;
        }
      }

      // Future dates: This/Next [day] based on Monday-Sunday week boundaries (only this week or immediate next week)
      if (diffDays > 1) {
        if (targetMonday.getTime() === todayMonday.getTime()) {
          return `This ${dayName}`;
        }
        const nextWeekMonday = new Date(todayMonday);
        nextWeekMonday.setDate(nextWeekMonday.getDate() + 7);
        if (targetMonday.getTime() === nextWeekMonday.getTime()) {
          return `Next ${dayName}`;
        }
      }

      // For all other dates (past or far future), use regular format without year
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (error) {
      return null;
    }
  };

  // Helper function to calculate days since last contact note
  const getDaysSinceLastContact = (account) => {
    if (!account) {
      return null;
    }

    // Helper to convert timestamp to Date
    const toDate = (timestamp) => {
      if (!timestamp) return null;
      // Handle Firestore Timestamp
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        return timestamp.toDate();
      }
      // Handle ISO string
      if (typeof timestamp === 'string') {
        return new Date(timestamp);
      }
      // Handle Date object
      if (timestamp instanceof Date) {
        return timestamp;
      }
      // Handle number (milliseconds)
      if (typeof timestamp === 'number') {
        return new Date(timestamp);
      }
      return null;
    };

    let lastContactDate = null;

    // First, check for lastEmailContactDate (from emails)
    if (account?.lastEmailContactDate) {
      lastContactDate = toDate(account.lastEmailContactDate);
    }

    // Then check contact notes (if no email contact date or if note is more recent)
    if (account?.notes && Array.isArray(account.notes) && account.notes.length > 0) {
      const contactNotes = account.notes.filter(note => note && (note.contact === true || note.contact === 'true'));
      
      if (contactNotes.length > 0) {
        // Get the most recent contact note
        const lastContactNote = contactNotes.reduce((latest, note) => {
          const noteDate = toDate(note.timestamp);
          const latestDate = toDate(latest?.timestamp);
          
          if (!noteDate) return latest || note;
          if (!latestDate) return note;
          
          return noteDate > latestDate ? note : latest;
        });

        if (lastContactNote?.timestamp) {
          const noteDate = toDate(lastContactNote.timestamp);
          // Use the most recent date between email contact and note contact
          if (!lastContactDate || (noteDate && noteDate > lastContactDate)) {
            lastContactDate = noteDate;
          }
        }
      }
    }

    // If no contact date found, return null
    if (!lastContactDate || isNaN(lastContactDate.getTime())) {
      return null;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    lastContactDate.setHours(0, 0, 0, 0);
    
    const diffTime = today - lastContactDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    // Return 0 if same day, otherwise return the day difference
    return diffDays >= 0 ? diffDays : 0;
  };

  // Helper function to get substatuses for current stage
  const getSubstatusesForStage = (stageName) => {
    return LEAD_SUBSTATUSES[stageName] || [];
  };

  // Helper function to check if a lead is flagged for needing contact
  const isLeadFlagged = (account) => {
    // Only check flags for Leads
    if (!account || account.type !== 'Leads') {
      return false;
    }

    // Don't flag lost leads
    if (account.stage === 'Lost' || account.result === 'Lost') {
      return false;
    }

    const daysUntilFlagged = CONTACT_FLAG_THRESHOLD_DAYS;
    const daysSinceContact = getDaysSinceLastContact(account);

    // If no contact notes, check days since creation
    if (daysSinceContact === null) {
      if (!account.createdAt) {
        return false;
      }

      // Helper to convert timestamp to Date
      const toDate = (timestamp) => {
        if (!timestamp) return null;
        if (timestamp.toDate && typeof timestamp.toDate === 'function') {
          return timestamp.toDate();
        }
        if (typeof timestamp === 'string') {
          return new Date(timestamp);
        }
        if (timestamp instanceof Date) {
          return timestamp;
        }
        if (typeof timestamp === 'number') {
          return new Date(timestamp);
        }
        return null;
      };

      const createdDate = toDate(account.createdAt);
      if (!createdDate) {
        return false;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      createdDate.setHours(0, 0, 0, 0);
      const diffTime = today - createdDate;
      const daysSinceCreation = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      return daysSinceCreation >= daysUntilFlagged;
    }

    // Check if days since contact exceeds threshold
    return daysSinceContact >= daysUntilFlagged;
  };

  useEffect(() => {
    fetchAccounts();
    
    // Set active tab based on URL
    if (location.pathname.includes('/leads')) {
      setActiveTab('Leads');
    } else if (location.pathname.includes('/clients')) {
      setActiveTab('Clients');
    }
  }, [location.pathname]);

  // Each internal user can star one view per tab: internal_users/{uid}.crmDefaultViews.{Clients|Leads}.
  const [defaultViews, setDefaultViews] = useState({});
  const [defaultViewsLoaded, setDefaultViewsLoaded] = useState(false);
  useEffect(() => {
    if (!currentUser?.uid) return;
    let cancelled = false;
    getDoc(doc(db, 'internal_users', currentUser.uid))
      .then(snap => { if (!cancelled) setDefaultViews(snap.data()?.crmDefaultViews || {}); })
      .catch(err => console.error('Failed to load starred CRM views:', err))
      .finally(() => { if (!cancelled) setDefaultViewsLoaded(true); });
    return () => { cancelled = true; };
  }, [currentUser?.uid]);

  const tabViews = TAB_VIEWS[activeTab] || TAB_VIEWS.Leads;
  const starredView = tabViews.some(view => view.value === defaultViews[activeTab]) ? defaultViews[activeTab] : null;
  // The starred view floats to the top of the dropdown; the rest keep their order.
  const orderedTabViews = starredView
    ? [...tabViews.filter(view => view.value === starredView), ...tabViews.filter(view => view.value !== starredView)]
    : tabViews;

  const selectView = value => {
    setSelectedStage(value);
    setViewMode(viewModeForSelection(value, activeTab));
  };

  // Clicking the star on the current view makes it the default; clicking it again clears it.
  const toggleStarredView = async () => {
    if (!currentUser?.uid) return;
    const tab = activeTab;
    const next = starredView === selectedStage ? null : selectedStage;
    setDefaultViews(prev => ({ ...prev, [tab]: next }));
    try {
      await updateDoc(doc(db, 'internal_users', currentUser.uid), {
        [`crmDefaultViews.${tab}`]: next ?? deleteField(),
      });
    } catch (err) {
      console.error('Failed to save starred CRM view:', err);
      setDefaultViews(prev => ({ ...prev, [tab]: starredView }));
    }
  };

  // Leads and Clients offer different dropdown options — start each tab on its starred view
  // (else the tab's first view: Stages for Leads, Lifecycle for Clients).
  useEffect(() => {
    selectView(starredView || tabViews[0].value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, defaultViewsLoaded]);

  // Incremented when accounts are fetched so Leads tab can sync lastEmailContactDate from Gmail once per load
  const accountsFetchIdRef = useRef(0);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAccounts();
      console.log('Fetched accounts:', data);
      setAccounts(data);
      accountsFetchIdRef.current += 1;
    } catch (err) {
      console.error('Error fetching accounts:', err);
      setError(`Failed to fetch accounts: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Sync lastEmailContactDate from current user's Gmail for all leads when viewing Leads tab (so kanban shows correct last contact days / flagged without opening each lead)
  useEffect(() => {
    if (activeTab !== 'Leads' || !currentUser?.uid || accounts.length === 0) return;
    const fetchId = accountsFetchIdRef.current;
    if (fetchId === 0) return;

    const leads = accounts.filter(a => a.type === 'Leads');
    if (leads.length === 0) return;

    let cancelled = false;
    const currentUserId = currentUser.uid;

    const syncLeadLastEmailContact = async (lead) => {
      if (cancelled) return;
      const leadEmail = lead?.contact?.email || lead?.email || lead?.discovery?.email;
      if (!leadEmail) return;

      const email = leadEmail.toLowerCase();
      let uniqueEmails = [];
      try {
        uniqueEmails = await fetchGmailMessagesForEmail(currentUserId, email, 50);
      } catch (err) {
        console.error(`Error fetching emails for lead ${lead.id}:`, err);
        return;
      }
      uniqueEmails.sort((a, b) => new Date(b.date) - new Date(a.date));

      const emailsToLead = uniqueEmails.filter(msg => {
        const recipients = msg.recipients || [];
        const toRecipients = msg.to ? msg.to.split(',').map(e => e.trim().toLowerCase()) : [];
        const ccRecipients = msg.cc ? msg.cc.split(',').map(e => e.trim().toLowerCase()) : [];
        const allRecipients = [...toRecipients, ...ccRecipients];
        const isFromLead = msg.from?.toLowerCase().includes(email);
        const isToLead = allRecipients.some(r => r.includes(email));
        return !isFromLead && isToLead;
      });

      if (emailsToLead.length === 0) return;
      const mostRecent = emailsToLead[0];
      const emailDate = new Date(mostRecent.date);
      const currentLast = lead?.lastEmailContactDate ? new Date(lead.lastEmailContactDate) : null;
      if (currentLast && emailDate <= currentLast) return;

      try {
        const updatedData = { ...lead, lastEmailContactDate: emailDate.toISOString() };
        await updateAccount(lead.id, updatedData);
        if (cancelled || accountsFetchIdRef.current !== fetchId) return;
        setAccounts(prev => prev.map(a => a.id === lead.id ? { ...a, lastEmailContactDate: emailDate.toISOString() } : a));
      } catch (err) {
        console.error('Error updating lastEmailContactDate for lead:', lead.id, err);
      }
    };

    const run = async () => {
      for (const lead of leads) {
        if (cancelled || accountsFetchIdRef.current !== fetchId) break;
        await syncLeadLastEmailContact(lead);
      }
    };
    run();

    return () => { cancelled = true; };
  }, [activeTab, currentUser?.uid, accounts.length]);

  // Get stages for current account type
  const getCurrentStages = () => {
    const currentType = ACCOUNT_TYPES.find(type => type.label === activeTab);
    return currentType?.defaultStages || [];
  };

  // Get color for stage
  const getStageColor = (stageName) => {
    const currentType = ACCOUNT_TYPES.find(type => type.label === activeTab);
    const stage = currentType?.defaultStages.find(s => s.title === stageName);
    console.log('Current stage:', stage);
    console.log('Stage color:', stage?.color);
    
    // Ensure the color is in the correct format (hex with #)
    const color = stage?.color?.startsWith('#') ? stage.color : `#${stage?.color}`;
    console.log('Formatted color:', color);
    
    return {
      text: `text-[${color || '#6B7280'}]`,
      border: `border-[${color || '#6B7280'}]`
    };
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'type') {
      // When account type changes, update the stage to the first stage of the new type
      const newType = ACCOUNT_TYPES.find(type => type.label === value);
      const newStage = newType?.defaultStages[0]?.title || '';
      
      setFormData(prev => ({
        ...prev,
        type: value,
        stage: newStage,
        substatus: '' // Reset substatus when type changes
      }));
    } else if (name === 'stage') {
      // When stage changes, reset substatus
      setFormData(prev => ({
        ...prev,
        stage: value,
        substatus: ''
      }));
    } else if (name === 'email') {
      // A business email (name@theircompany.com) fills in the website for them.
      // Only touch the website while it's empty or still the value we derived
      // from the previous email, so anything typed by hand is left alone.
      setFormData(prev => {
        const derivedFromPrev = websiteFromEmail(prev.email);
        const canAutoFill = !prev.website || prev.website === derivedFromPrev;
        return {
          ...prev,
          email: value,
          ...(canAutoFill ? { website: websiteFromEmail(value) } : {})
        };
      });
    } else if (name === 'addressLine') {
      // One line in, broken-out address/city/state/ZIP out.
      setFormData(prev => ({
        ...prev,
        addressLine: value,
        ...parseAddressLine(value)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleLeadLogoUpload = async (fileOrEvent) => {
    const file = fileOrEvent?.target?.files?.[0] || fileOrEvent;
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB.');
      return;
    }
    try {
      setUploadingImage(true);
      setError(null);
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop() || 'jpg';
      const fileName = `new-${timestamp}.${fileExtension}`;
      const storageRef = ref(storage, `lead-photos/${fileName}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      setFormData(prev => ({ ...prev, photoURL: downloadURL }));
    } catch (err) {
      console.error('Error uploading image:', err);
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleLogoDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (activeTab === 'Leads' && !uploadingImage) setIsDraggingOver(true);
  };

  const handleLogoDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const handleLogoDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
    if (activeTab !== 'Leads' || uploadingImage) return;
    const files = e.dataTransfer.files;
    if (files && files.length > 0) handleLeadLogoUpload(files[0]);
  };

  const handleAddAccount = async (e) => {
    e.preventDefault();
    console.log('Form submitted with data:', formData);
    try {
      setError(null); // Clear any previous errors
      
      // Validate required fields
      if (!formData.company) {
        setError('Please fill in the company name');
        return;
      }

      const accountData = {
        type: formData.type,
        stage: formData.stage,
        substatus: formData.substatus || null,
        company: formData.company,
        contact: {
          firstName: formData.firstName || '',
          lastName: formData.lastName || '',
          email: formData.email || '',
          phone: formData.phone || '',
          ...(formData.photoURL
            ? formData.type === 'Clients'
              ? { crmPhotoURL: formData.photoURL }
              : { photoURL: formData.photoURL }
            : {})
        },
        ...(formData.type === 'Clients' ? {
          address: formData.address || '',
          city: formData.city || '',
          state: formData.state || '',
          zipCode: formData.zipCode || '',
          country: 'US'
        } : {
          website: formData.website || '',
          // Optional on leads — only written when the address line was filled in.
          ...(formData.addressLine ? {
            address: formData.address || '',
            city: formData.city || '',
            state: formData.state || '',
            zipCode: formData.zipCode || '',
            country: 'US'
          } : {})
        }),
        estimatedProgram: formData.estimatedProgram || '',
        leadSource: formData.leadSource || 'Website',
        ...(formData.type === 'Leads' ? { signingChance: 'red' } : {}),
        projectIds: [],
        notes: [],
        createdAt: null,
        lastActivityAt: null
      };

      console.log('Account data being sent to Firestore:', accountData);

      // Call addAccount and wait for response
      const newAccount = await addAccount(accountData);
      console.log('New account created:', newAccount);

      if (!newAccount || !newAccount.id) {
        throw new Error('Failed to create account - no ID returned');
      }

      // Refresh the accounts list after adding new account
      await fetchAccounts();
      
      // Close modal and reset form
      setShowModal(false);
      setFormData({
        type: 'Leads',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        website: '',
        photoURL: '',
        addressLine: '',
        address: '',
        zipCode: '',
        city: '',
        state: '',
        stage: 'Lead Received',
        substatus: '',
        estimatedProgram: '',
        leadSource: 'Website'
      });
    } catch (err) {
      console.error('Error in handleAddAccount:', err);
      setError(`Failed to add account: ${err.message}`);
    }
  };

  const handleModalOpen = () => {
    setFormData(prev => ({
      ...prev,
      type: activeTab,
      stage: getCurrentStages()[0].title,
      substatus: ''
    }));
    setIsDraggingOver(false);
    setShowModal(true);
  };

  // Add search filter function (include contact, top-level, and discovery fields so prospects are searchable)
  const filterAccounts = (accounts) => {
    if (!searchQuery.trim()) return accounts;

    const query = searchQuery.toLowerCase().trim();
    return accounts.filter(account => {
      const searchableFields = [
        account.company,
        account.contact?.firstName,
        account.contact?.lastName,
        account.contact?.email,
        account.contact?.phone,
        account.email,
        account.website,
        account.discovery?.email,
        account.discovery?.firstName,
        account.discovery?.lastName,
        account.address,
        account.city,
        account.state,
        account.country,
        account.stage,
        account.type
      ].filter(Boolean).map(field => String(field).toLowerCase());

      return searchableFields.some(field => field.includes(query));
    });
  };

  // Update the accountsByStage logic to use dynamic stages
  const accountsByStage = {};
  const currentStages = getCurrentStages();
  const filteredAccounts = filterAccounts(accounts);
  
  // Filter out Archived stage from kanban view
  const kanbanStages = currentStages.filter(stage => {
    if (stage.title === 'Archived') return false;
    // Prospects stay available in the stage dropdown (table view) but are not a kanban column
    if (activeTab === 'Leads' && stage.title === 'Prospects') return false;
    return true;
  });

  kanbanStages.forEach(stage => {
    const stageAccounts = filteredAccounts.filter(account => {
      // First check if account matches type and stage
      const matches = account.type === activeTab && account.stage === stage.title;
      if (!matches) {
        return false;
      }

      // Ended clients (Lost / Canceled / Archived) keep their tier in `stage` but never sit in
      // a tier column — they live in the Lost table view.
      if (activeTab === 'Clients' && isEndedClient(account)) {
        return false;
      }

      // For Leads and Clients, filter out accounts where showOnKanban is false
      if (activeTab === 'Leads' || activeTab === 'Clients') {
        // Default to true if showOnKanban is not set (for backward compatibility)
        if (account.showOnKanban === false) {
          return false;
        }
      }
      
      console.log('Found matching account:', account);
      return true;
    });
    
    // Sort Discovery Call and Assessment stages by discovery call date (closest upcoming first)
    if ((stage.title === 'Discovery Call' || stage.title === 'Assessment') && activeTab === 'Leads') {
      stageAccounts.sort((a, b) =>
        compareByCallDate(a, b, getDiscoveryCallDate, 'discoveryCallDate')
      );
    }
    
    // Sort Onboarding Call and Proposal stages by onboarding call date (closest upcoming first)
    if ((stage.title === 'Onboarding Call' || stage.title === 'Proposal') && activeTab === 'Leads') {
      stageAccounts.sort((a, b) =>
        compareByCallDate(a, b, getOnboardingCallDate, 'onboardingCallDate')
      );
    }
    
    console.log(`Stage ${stage.title} has ${stageAccounts.length} accounts:`, stageAccounts);
    accountsByStage[stage.title] = stageAccounts;
  });

  const kanbanColumnWeight = (stageTitle) =>
    activeTab === 'Clients' && stageTitle === 'Custom' ? 2 : 1;

  const filledStagesWithCards = kanbanStages.filter(
    (stage) => (accountsByStage[stage.title] || []).length > 0
  );
  const filledWeightSum = filledStagesWithCards.reduce(
    (sum, s) => sum + kanbanColumnWeight(s.title),
    0
  );
  const filledCount = filledStagesWithCards.length;
  const emptyCount = Math.max(kanbanStages.length - filledCount, 0);

  const gapRem = 0.75;
  const emptyWidthRem = 3;
  const totalGapsRem = Math.max(kanbanStages.length - 1, 0) * gapRem;

  const filledColumnWidthForStage = (stageTitle) => {
    if (filledWeightSum <= 0) {
      return `calc((100% - ${totalGapsRem}rem) / ${kanbanStages.length || 1})`;
    }
    const w = kanbanColumnWeight(stageTitle);
    return `calc((100% - ${totalGapsRem}rem - ${emptyCount * emptyWidthRem}rem) * ${w} / ${filledWeightSum})`;
  };

  // Rows shown in the table view (individual stage selected from the dropdown)
  const isLostView = selectedStage === 'Lost';

  const tableAccounts = filteredAccounts
    .filter(account => {
      if (account.type !== activeTab) return false;

      // Lost is a filter-only view (no kanban column): an account counts as lost
      // via any marker, and hidden-from-kanban records still belong here.
      if (isLostView) return isLostAccount(account);

      if (selectedStage !== 'All' && account.stage !== selectedStage) return false;

      // For Leads and Clients, filter out accounts where showOnKanban is false
      if ((activeTab === 'Leads' || activeTab === 'Clients') && account.showOnKanban === false) {
        return false;
      }

      return true;
    })
    .sort((a, b) => {
      // Most recently lost first
      if (isLostView) {
        const dateA = getLostDate(a);
        const dateB = getLostDate(b);

        // Accounts without dates go to bottom
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;

        return dateB.getTime() - dateA.getTime();
      }

      // Sort by Discovery Call date when viewing Discovery Call stage
      if (activeTab === 'Leads' && selectedStage === 'Discovery Call') {
        return compareByCallDate(a, b, getDiscoveryCallDate, 'discoveryCallDate');
      }
      return 0; // No sorting for other stages
    });

  const selectedStageColor =
    currentStages.find(stage => stage.title === selectedStage)?.color || '#6B7280';

  const tierColor = (stageTitle) =>
    currentStages.find(stage => stage.title === stageTitle)?.color || '#6B7280';

  // Lifecycle + Map views: the same clients the Stages board shows (search applied,
  // hidden-from-kanban and ended clients left out). Ended ones feed the map's "past" toggle.
  const boardClients = activeTab === 'Clients'
    ? filteredAccounts.filter(account => account.type === 'Clients' && account.showOnKanban !== false)
    : [];
  const activeBoardClients = boardClients.filter(account => !isEndedClient(account));
  const pastBoardClients = boardClients.filter(account => isEndedClient(account));

  // Leads views: open leads (search applied; lost and hidden-from-kanban left out).
  const openLeads = activeTab === 'Leads'
    ? filteredAccounts.filter(account => account.type === 'Leads' && account.showOnKanban !== false && !isLostAccount(account))
    : [];
  // Leads ▸ Map: open leads narrowed by the stage + signing-chance chips, with every active
  // client as a toggleable layer (clients ignore the search box — they're the "is anyone close?" reference).
  const leadMatchesStage = account => mapStageFilter.has(account.stage);
  const leadMatchesChance = account => mapChanceFilter.has(normalizeLeadSigningChance(account));
  const mapLeads = openLeads.filter(account => leadMatchesStage(account) && leadMatchesChance(account));
  const mapFiltersAreDefault =
    mapChanceFilter.size === SIGNING_CHANCE_VALUES.length &&
    mapStageFilter.size === LEAD_MAP_DEFAULT_STAGES.length &&
    LEAD_MAP_DEFAULT_STAGES.every(stage => mapStageFilter.has(stage));
  const mapActiveClients = activeTab === 'Leads'
    ? accounts.filter(account => account.type === 'Clients' && account.showOnKanban !== false && !isEndedClient(account))
    : [];

  // Lifecycle + Lost boards share one card/column renderer (renderTenureBoard below).
  // Each item: { account, cornerLabel, cornerColor, cornerTitle, footer?, footerClass?, sortKey }.
  let tenureColumns = [];
  if (viewMode === 'lifecycle') {
    const now = new Date();
    tenureColumns = LIFECYCLE_BUCKETS.map(bucket => ({ ...bucket, items: [] }));
    activeBoardClients.forEach(account => {
      const tenure = describeTenure(account, now);
      const lc = resolveClientLifecycle(account, now);
      tenureColumns.find(c => c.id === tenure.bucket.id).items.push({
        account,
        cornerLabel: tenure.cornerLabel,
        cornerColor: tenure.bucket.color,
        cornerTitle: tenure.cornerTitle,
        footer: lc.state === 'Canceling' && lc.cancelEffectiveDate
          ? `Canceling ${lc.cancelEffectiveDate.toLocaleDateString()}`
          : null,
        footerClass: 'text-amber-600 dark:text-amber-400',
        // Newest client first; no start date sinks to the bottom
        sortKey: tenure.start ? -tenure.start.getTime() : Infinity,
      });
    });
  } else if (viewMode === 'prospects') {
    // Prospects (never a Stages column) grouped by how long they've been in the pipeline.
    const now = new Date();
    tenureColumns = PIPELINE_BUCKETS.map(bucket => ({ ...bucket, items: [] }));
    openLeads.filter(account => account.stage === 'Prospects').forEach(account => {
      const start = getLeadPipelineStart(account);
      const days = start ? Math.max(Math.floor((now.getTime() - start.getTime()) / DAY_MS), 0) : null;
      const bucket = start
        ? PIPELINE_BUCKETS.find(b => {
            const months = wholeMonthsSince(start, now);
            return months >= b.minMonths && months < b.maxMonths;
          })
        : PIPELINE_BUCKETS[0];
      const followUp = getLeadFollowUp(account, now);
      tenureColumns.find(c => c.id === bucket.id).items.push({
        account,
        cornerLabel: days === null ? 'No date' : `${days} day${days === 1 ? '' : 's'}`,
        cornerColor: bucket.color,
        cornerTitle: start ? `In the pipeline since ${start.toLocaleDateString()}` : 'No created date on file',
        footer: followUp ? `Follow up ${followUp.label}` : null,
        footerClass: followUp?.overdue ? 'text-red-600 dark:text-red-400' : 'text-blue-600 dark:text-blue-400',
        // Newest prospect first; no date sinks to the bottom
        sortKey: start ? -start.getTime() : Infinity,
      });
    });
  } else if (viewMode === 'lost') {
    // Ended clients — hidden-from-kanban records included, same as the old Lost table.
    const now = new Date();
    const lostClients = filteredAccounts.filter(account => account.type === 'Clients' && isEndedClient(account));
    const tierColumns = currentStages.map(stage => ({ id: stage.title, title: stage.title, color: stage.color, items: [] }));
    tenureColumns = lostGroupBy === 'lifecycle'
      ? LIFECYCLE_BUCKETS.map(bucket => ({ ...bucket, items: [] }))
      : tierColumns;
    lostClients.forEach(account => {
      const lc = resolveClientLifecycle(account, now);
      const endedAt = lc.endedAt || getLostDate(account);
      const tenure = describeTenure(account, endedAt || now);
      const tier = getProgramTier(account);
      let column;
      if (lostGroupBy === 'lifecycle') {
        column = tenureColumns.find(c => c.id === tenure.bucket.id);
      } else {
        column = tenureColumns.find(c => c.id === tier);
        if (!column) {
          // Hourly / A La Carte / Not Started get their own column after the main tiers
          column = { id: tier, title: tier, color: '#6B7280', items: [] };
          tenureColumns.push(column);
        }
      }
      const status = describeClientLifecycle(lc.state).label;
      column.items.push({
        account,
        cornerLabel: tenure.cornerLabel,
        cornerColor: tenure.bucket.color,
        cornerTitle: `Client for ${tenure.cornerLabel}${tenure.start ? ` · started ${tenure.start.toLocaleDateString()}` : ''}${endedAt ? ` · ended ${endedAt.toLocaleDateString()}` : ''}`,
        tier,
        footer: endedAt
          ? `${status} · ${endedAt.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}`
          : status,
        footerClass: 'text-red-600 dark:text-red-400',
        // Most recently ended first
        sortKey: endedAt ? -endedAt.getTime() : Infinity,
      });
    });
  }
  tenureColumns.forEach(column => column.items.sort((a, b) => a.sortKey - b.sortKey));

  console.log('Current active tab:', activeTab);
  console.log('All accounts:', accounts);
  console.log('Accounts by stage:', accountsByStage);

  const handleCardClick = (accountId) => {
    const account = accounts.find(acc => acc.id === accountId);
    if (account.type === 'Leads') {
      navigate(`/crm/lead/${accountId}`);
    } else if (account.type === 'Clients') {
      navigate(`/crm/client/${accountId}`);
    } else {
      // Fallback to the old route for backward compatibility
      navigate(`/crm/account/${accountId}`);
    }
  };

  const handleDragStart = (e, account) => {
    e.dataTransfer.setData('application/json', JSON.stringify(account));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleCopyStageEmails = async (stageTitle, accountsInStage) => {
    const seen = new Set();
    const emails = [];
    accountsInStage.forEach(account => {
      const email = account?.contact?.email || account?.email || account?.discovery?.email;
      if (!email) return;
      const key = email.trim().toLowerCase();
      if (!key || seen.has(key)) return;
      seen.add(key);
      emails.push(email.trim());
    });

    if (emails.length === 0) {
      setCopiedStage({ stage: stageTitle, count: 0 });
      setTimeout(() => setCopiedStage(null), 2000);
      return;
    }

    try {
      await navigator.clipboard.writeText(emails.join(', '));
      setCopiedStage({ stage: stageTitle, count: emails.length });
    } catch (err) {
      console.error('Error copying stage emails:', err);
      setCopiedStage({ stage: stageTitle, count: -1 });
    }
    setTimeout(() => setCopiedStage(null), 2000);
  };

  const handleDrop = async (e, newStage) => {
    e.preventDefault();
    const accountData = JSON.parse(e.dataTransfer.getData('application/json'));
    
    if (accountData.stage === newStage) return; // Don't update if dropped in same stage

    try {
      await updateAccountStage(accountData.id, newStage);
      // Refresh accounts after successful update
      await fetchAccounts();
    } catch (err) {
      console.error('Error updating account stage:', err);
      setError('Failed to update account stage');
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isMobile ? 'p-2' : isTablet ? 'p-3' : 'p-4'} relative`}>
      {/* Same ambient BBA background as the Dashboard and hiring board */}
      <PortalBackground />
      <div className="relative">
      {/* Header with buttons */}
      <div className="flex flex-col mb-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <BackButton onClick={() => navigate('/crm')} />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{activeTab}</h1>
          <div className="flex items-center space-x-2">
            {activeTab === 'Leads' && (
              <button
                onClick={() => setShowCheatsheet(true)}
                className="p-2 text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                title="Lead Substatus Cheatsheet"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className={`${isMobile ? 'mb-3 p-2' : 'mb-4 p-3'} bg-red-100 dark:bg-red-950 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 rounded`}>
            {error}
          </div>
        )}

        {/* Search bar, Stage filter, and Add New button */}
        <div className={`flex ${isMobile ? 'flex-col' : 'items-center'} ${isMobile ? 'gap-2' : 'gap-3'} mb-0`}>
          <div className={`relative ${isMobile ? 'w-full' : 'flex-1'}`}>
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="search"
              className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 pl-10 pr-10 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent [&::-webkit-search-cancel-button]:hidden"
              placeholder={`Search ${activeTab.toLowerCase()} by company, contact, or email…`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-700"
                title="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className={`relative ${isMobile ? 'w-full' : 'w-56'}`}>
            <button
              type="button"
              onClick={toggleStarredView}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              title={starredView === selectedStage ? 'Your default view — click to unstar' : 'Star this view as your default'}
              aria-pressed={starredView === selectedStage}
            >
              <Star
                className={`w-4 h-4 ${starredView === selectedStage ? 'text-amber-400' : 'text-gray-400'}`}
                fill={starredView === selectedStage ? 'currentColor' : 'none'}
              />
            </button>
            <select
              value={selectedStage}
              onChange={(e) => selectView(e.target.value)}
              className="w-full appearance-none rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 pl-10 pr-9 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              {/* Both tabs pick a view, not a single stage/tier — the Stages board already shows every one */}
              {orderedTabViews.map(view => (
                <option key={view.value} value={view.value}>
                  {view.label}{view.value === starredView ? '  ★' : ''}
                </option>
              ))}
            </select>
            <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          <AddButton onClick={handleModalOpen} />
        </div>
      </div>

      {/* Conditional rendering based on viewMode */}
      {viewMode === 'map' ? (
        <>
        {activeTab === 'Leads' && (
          /* Leads map filters — chip counts reflect the other filter, so they add up to what's mapped */
          <div className="mb-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 rounded-2xl border border-gray-200/80 dark:border-gray-800 bg-white/80 dark:bg-gray-900/60 px-4 py-3 shadow-sm">
            <div className="flex flex-wrap items-center justify-center gap-1.5">
              <span className="mr-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Stage</span>
              {getCurrentStages().map(stage => {
                const on = mapStageFilter.has(stage.title);
                const count = openLeads.filter(account => account.stage === stage.title && leadMatchesChance(account)).length;
                return (
                  <button
                    key={stage.title}
                    type="button"
                    onClick={() => toggleInSet(setMapStageFilter, stage.title)}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium transition-colors ${
                      on
                        ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 shadow-sm'
                        : 'border-transparent bg-gray-100 dark:bg-gray-800/50 text-gray-400 dark:text-gray-500'
                    }`}
                    aria-pressed={on}
                  >
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: on ? stage.color : '#D1D5DB' }} />
                    {stage.title}
                    <span className="text-xs opacity-70">{count}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center justify-center gap-1.5">
              <span className="mr-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Signing chance</span>
              {SIGNING_CHANCE_VALUES.map(value => {
                const on = mapChanceFilter.has(value);
                const meta = SIGNING_CHANCE_META[value];
                const count = openLeads.filter(account => normalizeLeadSigningChance(account) === value && leadMatchesStage(account)).length;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => toggleInSet(setMapChanceFilter, value)}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium transition-colors ${
                      on
                        ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 shadow-sm'
                        : 'border-transparent bg-gray-100 dark:bg-gray-800/50 text-gray-400 dark:text-gray-500'
                    }`}
                    aria-pressed={on}
                  >
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: on ? meta.dotColor : '#D1D5DB' }} />
                    {meta.label}
                    <span className="text-xs opacity-70">{count}</span>
                  </button>
                );
              })}
            </div>
            {!mapFiltersAreDefault && (
              <button
                type="button"
                onClick={() => {
                  setMapStageFilter(new Set(LEAD_MAP_DEFAULT_STAGES));
                  setMapChanceFilter(new Set(SIGNING_CHANCE_VALUES));
                }}
                className="text-sm font-medium text-red-600 dark:text-red-400 hover:underline"
              >
                Reset
              </button>
            )}
          </div>
        )}
        <Suspense
          fallback={
            <div className="flex justify-center py-24">
              <BBALoader size={64} />
            </div>
          }
        >
          <ClientMapView
            {...(activeTab === 'Clients'
              ? {
                  accounts: activeBoardClients,
                  noun: 'active client',
                  layers: [{ id: 'past', label: 'Past clients', noun: 'Past client', color: '#DC2626', accounts: pastBoardClients }],
                }
              : {
                  accounts: mapLeads,
                  noun: 'lead',
                  // Near-black ring so clients never read as a stage or signing-chance color
                  layers: [{ id: 'clients', label: 'Active clients', noun: 'Active client', color: '#111827', accounts: mapActiveClients }],
                  nearestLayerId: 'clients',
                  ringColorFor: (account) => tierColor(account.stage),
                  subtitleFor: (account) =>
                    [account.stage, `${SIGNING_CHANCE_META[normalizeLeadSigningChance(account)].label} chance`].filter(Boolean).join(' · '),
                })}
            key={activeTab}
            logoUrlFor={getCrmCompanyLogoDisplayUrl}
            onOpen={handleCardClick}
            isMobile={isMobile}
          />
        </Suspense>
        </>
      ) : viewMode === 'followup' ? (
        <Suspense
          fallback={
            <div className="flex justify-center py-24">
              <BBALoader size={64} />
            </div>
          }
        >
          <LeadFollowUpBoard
            leads={filteredAccounts.filter(account => account.type === 'Leads')}
            isLost={isLostAccount}
            logoUrlFor={getCrmCompanyLogoDisplayUrl}
            onOpen={handleCardClick}
            isMobile={isMobile}
            isTablet={isTablet}
          />
        </Suspense>
      ) : viewMode === 'lifecycle' || viewMode === 'lost' || viewMode === 'prospects' ? (
        /* Lifecycle board (active clients by tenure), Lost board (ended clients by program or
           tenure) and Prospects board (prospect leads by time in the pipeline) */
        <>
        {viewMode === 'lost' && (
          <div className="flex justify-center mb-3">
            <div className="inline-flex rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-1 shadow-sm">
              {[
                { id: 'program', label: 'By Program' },
                { id: 'lifecycle', label: 'By Lifecycle' },
              ].map(option => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setLostGroupBy(option.id)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    lostGroupBy === option.id
                      ? 'bg-red-600 text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className={`flex ${isMobile ? 'flex-col' : ''} gap-3 pb-4 ${isMobile ? '' : 'h-[calc(100vh-200px)]'}`}>
          {tenureColumns.map(column => (
            <div
              key={column.id}
              className={`${isMobile ? 'w-full' : 'flex-1 min-w-0'} rounded-2xl border border-gray-200/80 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-900/40 flex flex-col overflow-hidden`}
              style={{ minHeight: isMobile ? '200px' : '500px' }}
            >
              <div className="relative sticky top-0 z-20 flex items-center justify-between gap-2 px-4 py-3 bg-inherit">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: column.color }} />
                  <span className={`font-semibold ${isMobile ? 'text-xs' : 'text-sm'} text-gray-800 dark:text-gray-100 truncate`}>
                    {column.title}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopyStageEmails(column.title, column.items.map(item => item.account))}
                  title={`Copy all ${column.title} emails to clipboard`}
                  className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 shrink-0 cursor-pointer transition-transform hover:scale-110 active:scale-95"
                >
                  {column.items.length}
                </button>
                {copiedStage?.stage === column.title && (
                  <span className="absolute top-full left-1/2 -translate-x-1/2 mt-1 z-30 whitespace-nowrap rounded-md bg-gray-900 dark:bg-gray-700 px-2 py-1 text-xs font-medium text-white shadow-lg">
                    {copiedStage.count > 0
                      ? `Copied ${copiedStage.count} email${copiedStage.count === 1 ? '' : 's'}`
                      : copiedStage.count === 0
                        ? 'No emails to copy'
                        : 'Copy failed'}
                  </span>
                )}
              </div>
              <div
                className={`slim-scrollbar ${isMobile ? 'p-2 gap-2' : 'p-3 gap-3'} flex flex-col overflow-y-auto flex-1 overflow-x-hidden`}
                style={{ maxHeight: isMobile ? '400px' : 'calc(100vh - 300px)' }}
              >
                {column.items.length === 0 && (
                  <p className="py-8 text-center text-sm text-gray-400 dark:text-gray-500">
                    {viewMode === 'prospects' ? 'No prospects' : 'No clients'}
                  </p>
                )}
                {column.items.map(({ account, cornerLabel, cornerColor, cornerTitle, tier, footer, footerClass }) => {
                  const isLead = account.type === 'Leads';
                  const namesLine = isLead
                    ? `${(account.contact?.firstName || '').trim()} ${(account.contact?.lastName || '').trim()}`.trim()
                    : clientNamesLine(account, clientsExtraTeamByAccountId[account.id] || []);
                  const signingChance = isLead ? normalizeLeadSigningChance(account) : null;
                  const tierLabel = isLead ? null : tier || account.stage;
                  return (
                    <div
                      key={account.id}
                      className="group relative overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm w-full min-w-0 cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 flex-shrink-0"
                      onClick={() => handleCardClick(account.id)}
                    >
                      {/* Tenure — top-left, like the Leads days-since-contact pill */}
                      <div
                        className="pointer-events-none absolute top-1.5 left-2.5 z-10 px-2 py-0.5 text-xs font-semibold text-white rounded-full shadow-sm"
                        style={{ backgroundColor: cornerColor }}
                        title={cornerTitle}
                      >
                        {cornerLabel}
                      </div>
                      {tierLabel && (
                        <span
                          className="pointer-events-none absolute top-1.5 right-1.5 z-10 h-2.5 w-2.5 rounded-full ring-2 ring-white dark:ring-gray-700"
                          style={{ backgroundColor: tierColor(tierLabel) }}
                          title={tierLabel}
                        />
                      )}
                      {signingChance && (
                        <span
                          className="pointer-events-none absolute top-1.5 right-1.5 z-10 h-2.5 w-2.5 rounded-full ring-2 ring-white dark:ring-gray-700"
                          style={{ backgroundColor: SIGNING_CHANCE_META[signingChance].dotColor }}
                          title={`Signing chance: ${SIGNING_CHANCE_META[signingChance].label}`}
                        />
                      )}
                      <div className={`flex flex-col items-center w-full min-w-0 ${isMobile ? 'px-2 py-2.5' : 'px-3 py-3'}`}>
                        {isLead ? (
                          <LeadCrmAvatarCluster
                            account={account}
                            isMobile={isMobile}
                            tableLayout={false}
                            substatusIcon={getSubstatusIcon(account.substatus || 'New Lead')}
                          />
                        ) : (
                          <ClientCrmAvatarCluster
                            account={account}
                            extraTeamMembers={clientsExtraTeamByAccountId[account.id] || []}
                            isMobile={isMobile}
                            tableLayout={false}
                            maxExtraTeamMembers={1}
                          />
                        )}
                        <h4 className={`${isMobile ? 'text-sm' : 'font-medium'} w-full text-center text-gray-900 dark:text-gray-100 break-words [overflow-wrap:anywhere]`}>
                          {account.company}
                        </h4>
                        {namesLine && (
                          <p className={`${isMobile ? 'text-xs' : 'text-sm'} w-full text-center text-gray-500 dark:text-gray-400 break-words [overflow-wrap:anywhere]`}>
                            {namesLine}
                          </p>
                        )}
                        {footer && (
                          <p className={`mt-0.5 text-xs font-medium text-center ${footerClass}`}>{footer}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        </>
      ) : viewMode === 'kanban' ? (
        /* Existing Kanban board */
        <div className="flex gap-3 overflow-x-hidden pb-4 h-[calc(100vh-200px)]">
          {kanbanStages.map((stage, idx) => {
            const accountsInStage = accountsByStage[stage.title] || [];
            const hasAccounts = accountsInStage.length > 0;
            const stageColor = stage.color || '#6B7280';
            const customClientsTwoCol = activeTab === 'Clients' && stage.title === 'Custom';
            const isDragOver = dragOverStage === stage.title;
            return (
              <div
                key={stage.id}
                className={`${isMobile ? 'w-full' : 'flex-shrink-0 min-w-0'} rounded-2xl border flex flex-col box-border overflow-hidden transition-all duration-200 ${
                  isDragOver
                    ? 'border-transparent ring-2 bg-white/80 dark:bg-gray-800/80'
                    : 'border-gray-200/80 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-900/40'
                }`}
                style={{
                  minHeight: isMobile ? '300px' : '500px',
                  width: isMobile ? '100%' : hasAccounts ? filledColumnWidthForStage(stage.title) : `${emptyWidthRem}rem`,
                  ...(isDragOver ? { '--tw-ring-color': stageColor } : {}),
                }}
                onDragOver={(e) => {
                  handleDragOver(e);
                  if (dragOverStage !== stage.title) setDragOverStage(stage.title);
                }}
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget)) {
                    setDragOverStage((s) => (s === stage.title ? null : s));
                  }
                }}
                onDrop={(e) => {
                  setDragOverStage(null);
                  handleDrop(e, stage.title);
                }}
              >
                {hasAccounts ? (
                  <>
                    {/* Header */}
                    <div className="relative sticky top-0 z-20 flex items-center justify-between gap-2 px-4 py-3 bg-inherit">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: stageColor }} />
                        <span className={`font-semibold ${isMobile ? 'text-xs' : 'text-sm'} text-gray-800 dark:text-gray-100 truncate`}>
                          {stage.title}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleCopyStageEmails(stage.title, accountsInStage)}
                        title={`Copy all ${stage.title} emails to clipboard`}
                        className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 shrink-0 cursor-pointer transition-transform hover:scale-110 active:scale-95"
                      >
                        {accountsInStage.length}
                      </button>
                      {copiedStage?.stage === stage.title && (
                        <span className="absolute top-full left-1/2 -translate-x-1/2 mt-1 z-30 whitespace-nowrap rounded-md bg-gray-900 dark:bg-gray-700 px-2 py-1 text-xs font-medium text-white shadow-lg">
                          {copiedStage.count > 0
                            ? `Copied ${copiedStage.count} email${copiedStage.count === 1 ? '' : 's'}`
                            : copiedStage.count === 0
                              ? 'No emails to copy'
                              : 'Copy failed'}
                        </span>
                      )}
                    </div>
                    {/* Cards */}
                    <div
                      className={`slim-scrollbar ${isMobile ? 'p-2' : 'p-3'} min-w-0 ${
                        customClientsTwoCol
                          ? `grid grid-cols-[repeat(2,minmax(0,1fr))] auto-rows-max ${isMobile ? 'gap-2' : 'gap-3'}`
                          : `flex flex-col ${isMobile ? 'gap-2' : 'gap-3'}`
                      } overflow-y-auto flex-1 overflow-x-hidden`}
                      style={{ maxHeight: isMobile ? '400px' : 'calc(100vh - 300px)' }}
                    >
                      {accountsInStage.map(account => {
                        const isFlagged = isLeadFlagged(account);
                        const signingChance = account.type === 'Leads' ? normalizeLeadSigningChance(account) : null;
                        const signingDotColor = signingChance ? SIGNING_CHANCE_META[signingChance].dotColor : null;
                        return (
                        <div
                          key={account.id}
                          className={`group relative self-stretch overflow-hidden rounded-xl border shadow-sm w-full min-w-0 max-w-full cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 flex flex-shrink-0 ${
                            customClientsTwoCol
                              ? `${isMobile ? 'min-h-[7.25rem]' : 'min-h-[7.75rem]'} py-1`
                              : 'min-h-[48px]'
                          } ${
                            isFlagged
                              ? 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800/50'
                              : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800'
                          }`}
                          onClick={() => handleCardClick(account.id)}
                          draggable
                          onDragStart={(e) => handleDragStart(e, account)}
                        >
                          {account.type === 'Leads' && signingDotColor && (
                            <span
                              className="pointer-events-none absolute top-1.5 right-1.5 z-10 h-2.5 w-2.5 rounded-full ring-2 ring-white dark:ring-gray-700 shadow-sm"
                              style={{ backgroundColor: signingDotColor }}
                              title={`Signing chance: ${SIGNING_CHANCE_META[signingChance].label}`}
                              aria-hidden
                            />
                          )}
                          {/* Days Since Last Contact Pill - top-left, Leads only */}
                          {account.type === 'Leads' && (() => {
                            const daysSinceContact = getDaysSinceLastContact(account);
                            if (daysSinceContact === null || daysSinceContact === undefined) return null;
                            return (
                              <div
                                className="pointer-events-none absolute top-1.5 left-2.5 z-10 px-2 py-0.5 text-xs bg-red-600 dark:bg-red-700 text-white font-semibold rounded-full shadow-sm"
                                title="Days since last contact"
                              >
                                {daysSinceContact}
                              </div>
                            );
                          })()}
                          <div className={`flex-1 flex flex-col items-center justify-start w-full min-w-0 overflow-x-clip ${isMobile ? 'px-2 py-2.5' : 'px-3 py-3'}`}>
                            {/* Contact / logo / team (clients) or substatus icon (leads) */}
                            {account.type === 'Clients' ? (
                              <ClientCrmAvatarCluster
                                account={account}
                                extraTeamMembers={clientsExtraTeamByAccountId[account.id] || []}
                                isMobile={isMobile}
                                tableLayout={false}
                                maxExtraTeamMembers={1}
                              />
                            ) : (
                              <LeadCrmAvatarCluster
                                account={account}
                                isMobile={isMobile}
                                tableLayout={false}
                                substatusIcon={getSubstatusIcon(account.substatus || 'New Lead')}
                              />
                            )}
                            {/* Company name: wrap in narrow Custom column */}
                            <h4
                              className={`${isMobile ? 'text-sm' : 'font-medium'} w-full max-w-full min-w-0 text-center text-gray-900 dark:text-gray-100 break-words [overflow-wrap:anywhere] hyphens-auto`}
                              title={account.company || ''}
                            >
                              {account.company}
                            </h4>
                            {/* Lead contact name under company (matches Clients styling) */}
                            {account.type === 'Leads' && (() => {
                              const first = (account.contact?.firstName || '').trim();
                              const last = (account.contact?.lastName || '').trim();
                              const namesLine = `${first} ${last}`.trim();
                              if (!namesLine) return null;
                              return (
                                <p
                                  className={`${isMobile ? 'text-xs' : 'text-sm'} w-full max-w-full min-w-0 text-center text-gray-500 dark:text-gray-400 break-words [overflow-wrap:anywhere]`}
                                  title={namesLine}
                                >
                                  {namesLine}
                                </p>
                              );
                            })()}
                            {/* Client name(s): full name for one, first names when multiple */}
                            {account.type === 'Clients' && (() => {
                              const namesLine = clientNamesLine(account, clientsExtraTeamByAccountId[account.id] || []);
                              if (!namesLine) return null;
                              return (
                                <p
                                  className={`${isMobile ? 'text-xs' : 'text-sm'} w-full max-w-full min-w-0 text-center text-gray-500 dark:text-gray-400 break-words [overflow-wrap:anywhere]`}
                                  title={namesLine}
                                >
                                  {namesLine}
                                </p>
                              );
                            })()}
                            {account.type === 'Clients' && (() => {
                              const lc = resolveClientLifecycle(account);
                              if (lc.state === 'Active') return null;
                              const chip = describeClientLifecycle(lc.state);
                              const when = lc.state === 'Canceling' ? lc.cancelEffectiveDate : lc.endedAt;
                              return (
                                <span
                                  className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${chip.className}`}
                                  title={lc.reason ? `${chip.label} — ${lc.reason}` : chip.label}
                                >
                                  {chip.label}
                                  {when ? ` · ${when.toLocaleDateString()}` : ''}
                                </span>
                              );
                            })()}
                            {/* Discovery Call Date - Show for leads in Discovery Call or Assessment stages only */}
                            {account.type === 'Leads' && account.discoveryCallDate && (stage.title === 'Discovery Call' || stage.title === 'Assessment') && (() => {
                              const formattedDate = formatDiscoveryCallDate(account.discoveryCallDate);
                              let colorClass = '';
                              if (formattedDate === 'TBD') {
                                colorClass = 'bg-orange-500 dark:bg-orange-600 text-white';
                              } else if (formattedDate === 'Today') {
                                colorClass = 'bg-green-500 dark:bg-green-400 text-white';
                              } else if (formattedDate === 'Tomorrow') {
                                colorClass = 'bg-green-500 dark:bg-green-600 text-white';
                              } else if (formattedDate && formattedDate.startsWith('This ')) {
                                colorClass = 'bg-red-500 dark:bg-red-600 text-white';
                              } else {
                                colorClass = 'bg-yellow-500 dark:bg-yellow-600 text-white';
                              }
                              return (
                                <div className={`${isMobile ? 'mt-1 px-2 py-0.5 text-xs' : 'mt-1.5 px-2.5 py-1 text-xs'} ${colorClass} font-semibold rounded-full inline-block`}>
                                  {formattedDate}
                                </div>
                              );
                            })()}
                            {/* Onboarding Call Date - Show for leads in Onboarding Call or Proposal stages */}
                            {account.type === 'Leads' && account.onboardingCallDate && (stage.title === 'Onboarding Call' || stage.title === 'Proposal') && (() => {
                              const formattedDate = formatDiscoveryCallDate(account.onboardingCallDate);
                              if (!formattedDate) return null;
                              let colorClass = '';
                              if (formattedDate === 'TBD') {
                                colorClass = 'bg-orange-500 dark:bg-orange-600 text-white';
                              } else if (formattedDate === 'Today') {
                                colorClass = 'bg-green-500 dark:bg-green-400 text-white';
                              } else if (formattedDate === 'Tomorrow') {
                                colorClass = 'bg-green-500 dark:bg-green-600 text-white';
                              } else if (formattedDate && formattedDate.startsWith('This ')) {
                                colorClass = 'bg-red-500 dark:bg-red-600 text-white';
                              } else {
                                colorClass = 'bg-yellow-500 dark:bg-yellow-600 text-white';
                              }
                              return (
                                <div className={`${isMobile ? 'mt-1 px-2 py-0.5 text-xs' : 'mt-1.5 px-2.5 py-1 text-xs'} ${colorClass} font-semibold rounded-full inline-block`}>
                                  {formattedDate}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  /* Collapsed empty column: stage dot + zero count on top, stage
                     name reading top-to-bottom beneath (matches the hiring board palette) */
                  <div className="flex flex-col h-full items-center w-full">
                    <div className="flex flex-col items-center gap-2 px-2 py-3 w-full">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: stageColor }} />
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-700">
                        0
                      </span>
                    </div>
                    <div className="flex items-start justify-center pt-2">
                      <span
                        className={`font-semibold ${isMobile ? 'text-xs' : 'text-sm'} whitespace-nowrap text-gray-500 dark:text-gray-400`}
                        style={{ writingMode: 'vertical-rl' }}
                      >
                        {stage.title}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* Table View */
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
          {/* Stage summary row — count + copy all emails */}
          <div className="relative flex items-center justify-center gap-2.5 px-5 py-4 border-b border-gray-200 dark:border-gray-800">
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: selectedStageColor }} />
            <span className="text-base font-semibold text-gray-900 dark:text-white">
              {selectedStage === 'All' ? `All ${activeTab}` : selectedStage}
            </span>
            <button
              type="button"
              onClick={() => handleCopyStageEmails(selectedStage, tableAccounts)}
              title={`Copy all ${selectedStage === 'All' ? activeTab : selectedStage} emails to clipboard`}
              className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 cursor-pointer transition-transform hover:scale-110 active:scale-95"
            >
              {tableAccounts.length}
            </button>
            {copiedStage?.stage === selectedStage && (
              <span className="absolute top-full left-1/2 -translate-x-1/2 mt-1 z-30 whitespace-nowrap rounded-md bg-gray-900 dark:bg-gray-700 px-2 py-1 text-xs font-medium text-white shadow-lg">
                {copiedStage.count > 0
                  ? `Copied ${copiedStage.count} email${copiedStage.count === 1 ? '' : 's'}`
                  : copiedStage.count === 0
                    ? 'No emails to copy'
                    : 'Copy failed'}
              </span>
            )}
          </div>
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Company</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Email</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Phone</th>
                {activeTab === 'Leads' && selectedStage === 'Discovery Call' && (
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Discovery Call Date</th>
                )}
                {isLostView ? (
                  <>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Lost Date</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Portal Status</th>
                  </>
                ) : (
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Stage</th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {tableAccounts
                .map((account) => {
                  const isFlagged = isLeadFlagged(account);
                  return (
                  <tr 
                    key={account.id} 
                    onClick={() => handleCardClick(account.id)}
                    className={`cursor-pointer ${
                      isFlagged 
                        ? 'bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/40' 
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <td className="px-6 py-4 min-w-0 max-w-[280px] lg:max-w-none">
                      <div className="flex items-center min-w-0">
                        <div className="flex-shrink-0 flex items-center justify-center">
                          {account.type === 'Clients' ? (
                            <ClientCrmAvatarCluster
                              account={account}
                              extraTeamMembers={clientsExtraTeamByAccountId[account.id] || []}
                              isMobile={false}
                              tableLayout
                            />
                          ) : (
                            <LeadCrmAvatarCluster
                              account={account}
                              isMobile={false}
                              tableLayout
                              substatusIcon={getSubstatusIcon(account.substatus || 'New Lead')}
                            />
                          )}
                        </div>
                        <div className="ml-4 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {account.contact?.firstName} {account.contact?.lastName}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">{account.company}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">{account.contact?.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">{account.contact?.phone}</div>
                    </td>
                    {isLostView && (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-white">
                            {getLostDate(account)?.toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            }) || 'No date'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {(() => {
                            // Same flag the portal itself reads (usePortalAccess -> lockStatus)
                            const isLocked = account.lockStatus === 'locked';
                            return (
                              <span
                                className={`px-2 py-1 inline-flex items-center gap-1.5 text-xs leading-5 font-semibold rounded-full ${
                                  isLocked
                                    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                    : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                }`}
                              >
                                {isLocked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
                                {isLocked ? 'Locked' : 'Has Access'}
                              </span>
                            );
                          })()}
                        </td>
                      </>
                    )}
                    {activeTab === 'Leads' && selectedStage === 'Discovery Call' && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isDiscoveryCallToday(account.discoveryCallDate) ? (
                          <div className="text-sm font-semibold text-green-500 dark:text-green-400">
                            Today
                          </div>
                        ) : (
                          <div className="text-sm text-gray-900 dark:text-white">
                            {formatDiscoveryCallDate(account.discoveryCallDate) || 'No date set'}
                          </div>
                        )}
                      </td>
                    )}
                    {!isLostView && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          stageColorsByType[activeTab][account.stage]?.text.replace('text-', 'bg-').replace('600', '100')
                        } ${stageColorsByType[activeTab][account.stage]?.text} dark:bg-opacity-20`}>
                          {account.stage}
                        </span>
                      </td>
                    )}
                  </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}
      </div>

      {/* New Account Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-4xl overflow-y-auto max-h-screen">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div className="flex-1"></div>
                <h2 className="text-xl font-semibold text-center flex-1 text-gray-900 dark:text-white">New {activeTab.slice(0, -1)}</h2>
                <button 
                  onClick={() => setShowModal(false)} 
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 flex-1 flex justify-end"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleAddAccount} className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} ${isMobile ? 'gap-3' : 'gap-6'}`}>
                {/* Account Type and Stage */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Account Type</label>
                  <select 
                    name="type" 
                    value={formData.type} 
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {ACCOUNT_TYPES.map(type => (
                      <option key={type.id} value={type.label}>{type.label}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Stage</label>
                  <select 
                    name="stage" 
                    value={formData.stage} 
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {getCurrentStages().map(stage => (
                      <option key={stage.id} value={stage.title}>{stage.title}</option>
                    ))}
                  </select>
                </div>


                {/* Company Information */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Company Name *</label>
                  <input 
                    type="text" 
                    name="company" 
                    value={formData.company} 
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>

                {/* Contact Information */}
                <div className="col-span-2">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Primary Contact</h3>
                </div>

                {/* Logo upload - Leads only */}
                {activeTab === 'Leads' && (
                  <div className="col-span-2 flex flex-col sm:flex-row items-start gap-4">
                    <div
                      className="cursor-pointer flex-shrink-0"
                      onDragOver={handleLogoDragOver}
                      onDragLeave={handleLogoDragLeave}
                      onDrop={handleLogoDrop}
                    >
                      <div className={`w-24 h-24 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden transition-all ${
                        isDraggingOver ? 'ring-4 ring-blue-500 ring-offset-2 scale-105' : ''
                      }`}>
                        {uploadingImage ? (
                          <div className="flex flex-col items-center justify-center">
                            <BBALoader size={24} />
                            <span className="text-xs text-gray-500 mt-1">Uploading...</span>
                          </div>
                        ) : isDraggingOver ? (
                          <div className="flex flex-col items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            <span className="text-xs text-blue-500 mt-1 font-medium">Drop here</span>
                          </div>
                        ) : formData.photoURL ? (
                          <img src={formData.photoURL} alt="Logo" className="w-full h-full object-cover" />
                        ) : (
                          <label className="cursor-pointer flex flex-col items-center justify-center w-full h-full">
                            <input type="file" accept="image/*" className="hidden" onChange={handleLeadLogoUpload} disabled={uploadingImage} />
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="text-xs text-gray-500 mt-1">Logo</span>
                          </label>
                        )}
                      </div>
                      {formData.photoURL && (
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, photoURL: '' }))}
                          className="text-xs text-red-600 hover:text-red-700 mt-1 block"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4 min-w-0">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">First Name</label>
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Last Name</label>
                        <input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {activeTab !== 'Leads' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">First Name</label>
                      <input 
                        type="text" 
                        name="firstName" 
                        value={formData.firstName} 
                        onChange={handleInputChange}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Last Name</label>
                      <input 
                        type="text" 
                        name="lastName" 
                        value={formData.lastName} 
                        onChange={handleInputChange}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Email</label>
                  <input 
                    type="email" 
                    name="email" 
                    value={formData.email} 
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Phone</label>
                  <input 
                    type="tel" 
                    name="phone" 
                    value={formData.phone} 
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Website - Leads only */}
                {activeTab === 'Leads' && (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Website</label>
                    <input 
                      type="url" 
                      name="website" 
                      value={formData.website} 
                      onChange={handleInputChange}
                      placeholder="https://"
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                )}

                {/* Address - Leads only, optional single line split into parts */}
                {activeTab === 'Leads' && (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                      Address <span className="text-gray-400 dark:text-gray-500 font-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      name="addressLine"
                      value={formData.addressLine}
                      onChange={handleInputChange}
                      placeholder="123 Main St, Durham, NC 27701"
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    {formData.addressLine.trim() && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {[
                          formData.address && `Street: ${formData.address}`,
                          formData.city && `City: ${formData.city}`,
                          formData.state && `State: ${formData.state}`,
                          formData.zipCode && `ZIP: ${formData.zipCode}`
                        ].filter(Boolean).join('  •  ') || 'Enter a street, city, state and ZIP to split it out'}
                      </p>
                    )}
                  </div>
                )}

                {/* Address Information - Clients only */}
                {activeTab === 'Clients' && (
                  <>
                    <div className="col-span-2">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Address</h3>
                    </div>

                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Street Address</label>
                      <input 
                        type="text" 
                        name="address" 
                        value={formData.address} 
                        onChange={handleInputChange}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">City</label>
                      <input 
                        type="text" 
                        name="city" 
                        value={formData.city} 
                        onChange={handleInputChange}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">State</label>
                      <input 
                        type="text" 
                        name="state" 
                        value={formData.state} 
                        onChange={handleInputChange}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">ZIP Code</label>
                      <input 
                        type="text" 
                        name="zipCode" 
                        value={formData.zipCode} 
                        onChange={handleInputChange}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  </>
                )}

                {/* Submit button */}
                <div className="col-span-2 flex justify-end mt-4">
                  <button 
                    type="submit"
                    className="bg-red-600 text-white px-5 py-2 rounded hover:bg-red-800"
                  >
                    Create {activeTab.slice(0, -1)}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}


      {/* Lead Substatus Cheatsheet */}
      <LeadSubstatusCheatsheet 
        isOpen={showCheatsheet} 
        onClose={() => setShowCheatsheet(false)} 
      />
    </div>
  );
};

export default Accounts; 